import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Play, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { settingsApi, ApiError } from '../../lib/api';

export default function SimulationTools() {
  const { currentUser: user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState('');

  const handleSimulateWarRoom = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError('');
      setResult(null);
      const data = await settingsApi.simulate() as any;
      if (data?.status === 'success') {
        setResult(data.strategy);
      } else {
        setError(data?.message || `Simulation failed (raw response: ${JSON.stringify(data).slice(0, 300)})`);
      }
    } catch (err: any) {
      // Surface the real error so we can diagnose: ApiError detail, generic
      // Error message, or fully-stringified value as last resort.
      console.error('[SimulationTools] War Room execution failed:', err);
      let msg: string;
      if (err instanceof ApiError) {
        msg = `API ${err.status || ''}: ${err.detail || '(no detail)'}`;
      } else if (err instanceof Error) {
        msg = `${err.name}: ${err.message || '(no message)'}`;
      } else {
        msg = String(err);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Simulation Mode</h2>
        <p className="text-sm text-muted-foreground">Test platform execution paths without risking live capital or waiting for scheduled events.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-md flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-primary" />
            War Room Instant Execution
          </CardTitle>
          <CardDescription className="text-xs">
            Bypass the pre-market schedule and force the Daily War Room to execute right now using current market data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <>
            <Button
              onClick={handleSimulateWarRoom}
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Execute War Room Now
            </Button>

            {error && (
              <div className="p-3 bg-red-900/20 text-red-400 border border-red-900/50 rounded mt-4">{error}</div>
            )}

            {result && (
              <div className="mt-4 p-4 bg-muted/30 rounded border overflow-auto max-h-[400px]">
                <h3 className="text-sm font-semibold mb-2">Simulation Output:</h3>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </div>
            )}
          </>
        </CardContent>
      </Card>
    </div>
  );
}
